/**
 * WebSocketContext
 *
 * Maintains exactly ONE WebSocket connection per authenticated user and
 * dispatches each incoming message to all registered subscribers.
 *
 * SPLIT INTO TWO CONTEXTS for performance:
 *   - WebSocketActionsContext: subscribe / sendMessage / reconnect /
 *     fetchUnreadMessages. These callbacks are stable (useCallback []),
 *     so consumers NEVER re-render from this context.
 *   - WebSocketStateContext: connected / unreadCount. Only components
 *     that actually display this state should consume it.
 *
 * Most components only need to subscribe to events (via useWebSocket(handler)),
 * which means they only consume the stable Actions context — zero re-renders
 * when WS state ticks.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketActionsContext = createContext(null);
const WebSocketStateContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const subscribersRef = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isReconnecting = useRef(false);
  const maxReconnectAttempts = 10;
  const [connected, setConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const subscribe = useCallback((handler) => {
    subscribersRef.current.add(handler);
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);

  const broadcast = useCallback((data) => {
    subscribersRef.current.forEach((handler) => {
      try {
        handler(data);
      } catch (e) {
        console.error('WebSocket subscriber error:', e);
      }
    });
  }, []);

  const sendMessage = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  const fetchUnreadMessages = useCallback(() => {
    sendMessage({ type: 'fetch_unread' });
  }, [sendMessage]);

  const confirmMessageReceived = useCallback(
    (messageId) => {
      sendMessage({ type: 'message_received', message_id: messageId });
    },
    [sendMessage]
  );

  const connect = useCallback(() => {
    if (!user?.user_id) return;

    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${backendUrl.replace(/^https?:\/\//, '')}/api/ws/${user.user_id}`;

    console.log('[WS] Connecting (singleton):', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[WS] Connected');
        setConnected(true);
        reconnectAttempts.current = 0;

        if (isReconnecting.current) {
          setTimeout(() => fetchUnreadMessages(), 500);
          isReconnecting.current = false;
        }

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
            } catch (e) {
              /* noop */
            }
          }
        }, 20000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type !== 'pong' && data.type !== 'ping') {
            console.log('[WS] Message:', data.type);
          }

          // Stash unread count from initial connect handshake
          if (data.type === 'connected') {
            setUnreadCount(data.unread_count || 0);
            return;
          }

          // Auto-confirm receipt for new messages (delivery tracking)
          if (data.type === 'new_message' && data.message?.id) {
            confirmMessageReceived(data.message.id);
          }

          // Skip noise
          if (data.type === 'pong' || data.type === 'ping') return;

          // Fan out to subscribers
          broadcast(data);
        } catch (error) {
          console.error('[WS] Parse error:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code);
        setConnected(false);
        wsRef.current = null;
        isReconnecting.current = true;

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            1000 * Math.pow(1.5, reconnectAttempts.current),
            30000
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current += 1;
            connect();
          }, delay);
        } else {
          toast.error('Connection lost. Please refresh the page.', {
            duration: 10000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
          });
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WS] Failed to create:', error);
      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current += 1;
          connect();
        }, 3000);
      }
    }
  }, [user?.user_id, broadcast, confirmMessageReceived, fetchUnreadMessages]);

  const reconnect = useCallback(() => {
    if (wsRef.current) wsRef.current.close();
    reconnectAttempts.current = 0;
    isReconnecting.current = true;
    connect();
  }, [connect]);

  // Initial connect + visibility/online handlers
  useEffect(() => {
    if (!user?.user_id) {
      // Logged out — tear down
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore close errors during teardown
        }
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      setConnected(false);
      return;
    }

    connect();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (
          !wsRef.current ||
          wsRef.current.readyState !== WebSocket.OPEN
        ) {
          reconnectAttempts.current = 0;
          isReconnecting.current = true;
          connect();
        } else {
          fetchUnreadMessages();
        }
      }
    };

    const handleOnline = () => {
      reconnectAttempts.current = 0;
      isReconnecting.current = true;
      connect();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      // Note: do NOT close ws here — provider stays mounted for app lifetime.
      // Cleanup only fires on real provider unmount.
    };
  }, [user?.user_id, connect, fetchUnreadMessages]);

  // Request notification permission once
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Actions: stable callbacks. This memo's deps are all stable refs, so
  // the value object is created exactly once and never invalidates the
  // consumers that subscribe to actions only.
  const actionsValue = useMemo(
    () => ({
      subscribe,
      sendMessage,
      reconnect,
      fetchUnreadMessages,
    }),
    [subscribe, sendMessage, reconnect, fetchUnreadMessages]
  );

  // State: changes on WS open/close + initial unread snapshot. Only
  // components that actually display this state should consume it.
  const stateValue = useMemo(
    () => ({ connected, unreadCount }),
    [connected, unreadCount]
  );

  return (
    <WebSocketActionsContext.Provider value={actionsValue}>
      <WebSocketStateContext.Provider value={stateValue}>
        {children}
      </WebSocketStateContext.Provider>
    </WebSocketActionsContext.Provider>
  );
};

export const useWebSocketActions = () => {
  const ctx = useContext(WebSocketActionsContext);
  if (!ctx) {
    throw new Error('useWebSocketActions must be used within WebSocketProvider');
  }
  return ctx;
};

export const useWebSocketState = () => {
  const ctx = useContext(WebSocketStateContext);
  if (!ctx) {
    throw new Error('useWebSocketState must be used within WebSocketProvider');
  }
  return ctx;
};

// Backwards-compat for any caller that still wants the merged shape.
// New code should prefer useWebSocketActions or useWebSocketState directly.
export const useWebSocketContext = () => {
  const actions = useWebSocketActions();
  const state = useWebSocketState();
  return useMemo(() => ({ ...actions, ...state }), [actions, state]);
};

/**
 * useWebSocketSubscribe — subscribe-only escape hatch for INFRASTRUCTURE
 * code (other contexts, providers) that cares only about events and never
 * about connection status. Consumes the stable Actions context only, so
 * a state tick (rare WS open/close) does NOT cause the calling component
 * to re-render. This is what PresenceContext uses to avoid cascading
 * re-renders through the whole app tree on connect/disconnect events.
 */
export const useWebSocketSubscribe = (onMessage) => {
  const { subscribe } = useWebSocketActions();
  const onMessageRef = React.useRef(onMessage);
  React.useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  React.useEffect(() => {
    if (typeof onMessageRef.current !== 'function') return undefined;
    const unsubscribe = subscribe((data) => {
      const handler = onMessageRef.current;
      if (typeof handler === 'function') handler(data);
    });
    return unsubscribe;
  }, [subscribe]);
};

export default WebSocketActionsContext;
